import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import DatePicker from "react-multi-date-picker";

const Pages = () => {
    const BaseUrl = process.env.REACT_APP_API_BASE_URL;
    const [showGenerate, setShowGenerate] = useState(true);
    const [showTable, setShowTable] = useState(false);

    const [schedules, setSchedules] = useState([]);
    const [deliveryPoints, setDeliveryPoints] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [branchId, setBranchId] = useState(null);
    const [showValidation, setShowValidation] = useState(false);

    const [deliveryData, setDeliveryData] = useState({
        catering_order: [],
        corp_order: [],
        subscription_order: []
    });

    // Helper to safely render values
    const getValue = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') return JSON.stringify(val);
        return val;
    };

    const fetchSchedules = async () => {
        try {
            const res = await axios.get(`${BaseUrl}/item-schedule/items/active`);
            if (res.data && res.data.data) {
                const options = res.data.data.map(sch => ({
                    value: sch.sno,
                    label: sch.itemschedule_name
                }));
                setSchedules(options);
            }
        } catch (err) {
            console.error("Error fetching schedules:", err);
        }
    };

    const fetchDeliveryPoints = async () => {
        try {
            const res = await axios.get(`${BaseUrl}/dropdown/deliverypoint`, {
                params: { branch_id: branchId }
            });
            if (res.data && res.data.data) {
                const options = res.data.data.map(dp => ({
                    value: dp.sno,
                    label: dp.delivery_point
                }));
                setDeliveryPoints(options);
            }
        } catch (err) {
            console.error("Error fetching delivery points:", err);
        }
    };

    const handleGenerateClick = () => {
        setShowGenerate(false);
        fetchSchedules();
        fetchDeliveryPoints();
    };

    const handleFilter = async () => {
        if (!selectedDate || !selectedSchedule || !selectedDeliveryPoint) {
            setShowValidation(true);
            return;
        }
        setShowValidation(false);

        try {
            const res = await axios.get(`${BaseUrl}/catcorp-deliveries/${selectedDate}`, {
                params: { branch_id: branchId }
            });
            if (res.data) {
                setDeliveryData({
                    catering_order: res.data.catering_order || [],
                    corp_order: res.data.corp_order || [],
                    subscription_order: res.data.subscription_order || []
                });
            } else {
                setDeliveryData({
                    catering_order: [],
                    corp_order: [],
                    subscription_order: []
                });
            }
        } catch (err) {
            console.error("Error fetching delivery list:", err);
            setDeliveryData({
                catering_order: [],
                corp_order: [],
                subscription_order: []
            });
        }

        setShowTable(true);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <>
            {/* Breadcrumb */}
            <div className="d-flex align-items-center justify-content-between page-titles px-0 py-1 mx-0">
                <div className='mb-0'>
                    <div className="mb-0 px-0">
                        <h4 className='breadcrumb-header'>Manage Delivery Sheets</h4>
                    </div>
                    <ol className="breadcrumb fs-7">
                        <li className="breadcrumb-item"><Link to='/dashboard'>Dashboard</Link></li>
                        <li className="breadcrumb-item"><i className="fa-solid fa-angles-right fs-8"></i></li>
                        <li className="breadcrumb-item active">Production Management</li>
                    </ol>
                </div>
            </div>

            {/* Initial Generate */}
            {showGenerate && (
                <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                    <h2>Generate Delivery Sheet</h2>
                    <p className="text-center text-danger" style={{ fontSize: "20px" }}>
                        Click the button below to generate a new delivery sheet for today.
                    </p>
                    <Button variant="primary" onClick={handleGenerateClick}>Generate</Button>
                </div>
            )}

            {/* After Generate */}
            {!showGenerate && (
                <>
                    {/* Filters */}
                    <div className="row mb-3 gx-2 align-items-end">
                        <div className="col-lg-3 mb-2">
                            <label className="text-black font-w500 fs-7">Date <span className="required">*</span></label>
                            <div className="border rounded px-2 py-1">
                                <DatePicker
                                    value={selectedDate}
                                    onChange={setSelectedDate}
                                    format="YYYY-MM-DD"
                                    className="w-100 border-0"
                                />
                            </div>
                            {showValidation && !selectedDate && (
                                <span className="text-danger fs-7">This field is required</span>
                            )}
                        </div>

                        <div className="col-lg-3 mb-2">
                            <label className="text-black font-w500 fs-7">Schedule <span className="required">*</span></label>
                            <Select
                                value={selectedSchedule}
                                onChange={setSelectedSchedule}
                                options={schedules}
                                placeholder="Select Schedule"
                            />
                            {showValidation && !selectedSchedule && (
                                <span className="text-danger fs-7">This field is required</span>
                            )}
                        </div>

                        <div className="col-lg-3 mb-2">
                            <label className="text-black font-w500 fs-7">Delivery Point <span className="required">*</span></label>
                            <Select
                                value={selectedDeliveryPoint}
                                onChange={setSelectedDeliveryPoint}
                                options={deliveryPoints}
                                placeholder="Select Delivery Point"
                            />
                            {showValidation && !selectedDeliveryPoint && (
                                <span className="text-danger fs-7">This field is required</span>
                            )}
                        </div>

                        <div className="col-auto d-flex gap-2 mb-2">
                            <Button variant="primary" size="sm" onClick={handleFilter}>Filter</Button>
                            <Button variant="secondary" size="sm" onClick={handlePrint}>Print</Button>
                        </div>
                    </div>

                   {showTable && (
                        <div className="table-container border rounded p-3" style={{ maxHeight: '900px' }}>
                            {/* Table Header with Date */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2 className="mb-0">Delivery Sheet</h2>
                                <div>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={setSelectedDate}
                                        format="YYYY-MM-DD"
                                    />
                                </div>
                            </div>

                            {/* Scrollable table */}
                            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <table className="table align-middle table-row-dashed table-striped table-hover fs-7 gy-1 gs-2 dataTable mb-0">
                                    <thead>
                                        <tr className="text-start text-muted fw-bold fs-6 gs-0 th_head">
                                            <th>S.No</th>
                                            <th>Customer Name</th>
                                            <th>Area</th>
                                            <th>Address</th>
                                            <th>Contact</th>
                                            <th>Dates</th>
                                            <th>Total Count</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 fw-semibold fs-7">
                                        {["catering_order", "corp_order", "subscription_order"].map((orderType) => (
                                            deliveryData[orderType].length > 0 ? (
                                            <React.Fragment key={orderType}>
                                                {/* Section Header */}
                                                <tr style={{ color: 'brown', fontWeight: 'bold', fontSize: '16px' }}>
                                                <th colSpan="7" className="bg-light">
                                                    {orderType === "catering_order" 
                                                    ? "Catering Orders" 
                                                    : orderType === "corp_order" 
                                                    ? "Corporate Orders" 
                                                    : "Subscription Orders"}
                                                </th>
                                                </tr>

                                                {/* Section Rows */}
                                                {deliveryData[orderType].map((item, index) => {
                                                const customer = orderType === "catering_order" 
                                                    ? item.catering_customer 
                                                    : orderType === "corp_order" 
                                                    ? item.corporate_customer 
                                                    : null;

                                                return (
                                                    <tr key={`${orderType}-${index}`}>
                                                    <td className="text-black">{index + 1}</td>
                                                    <td className="text-black">{customer?.customer_name || '-'}</td>
                                                    <td className="text-black">{customer?.area || '-'}</td>
                                                    <td className="text-black">{customer?.address || 'Periya Swamy Kovil Street, Madurai'}</td>
                                                    <td className="text-black">{customer?.customer_number || '-'}</td>
                                                    <td className="text-black">{item.catcorp_delivery_date
                                                        ? new Date(item.catcorp_delivery_date).toLocaleDateString('en-GB', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit'
                                                        }).replace(/ /g, '-') 
                                                        : '-'}</td>
                                                    <td className="text-black text-center">1</td>
                                                    </tr>
                                                );
                                                })}
                                            </React.Fragment>
                                            ) : null
                                        ))}

                                        {/* If all orders are empty */}
                                        {["catering_order", "corp_order", "subscription_order"].every(
                                            orderType => deliveryData[orderType].length === 0
                                        ) && (
                                            <tr>
                                            <td colSpan="7" className="text-center">No data found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Only show signatures if there's any data */}
                            {["catering_order", "corp_order", "subscription_order"].some(
                                orderType => deliveryData[orderType].length > 0
                            ) && (
                            <div className="d-flex justify-content-between mt-5">
                                {/* Manager Signature */}
                                <div className="text-center text-black fw-bold" style={{ width: '200px', marginTop: '80px' }}>
                                    <div className="border-top pt-2">Manager Signature</div>
                                    <div className="mb-2 d-flex justify-content-center gap-2">
                                        <button className="btn btn-sm btn-outline-primary">
                                            <i className="fas fa-upload"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger">
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Watchman Signature */}
                                <div className="text-center text-black fw-bold" style={{ width: '200px', marginTop: '80px' }}>
                                    <div className="border-top pt-2">Security Signature</div>
                                    <div className="mb-2 d-flex justify-content-center gap-2">
                                        <button className="btn btn-sm btn-outline-primary">
                                            <i className="fas fa-upload"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger">
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}

                </>
            )}
        </>
    );
};

export default Pages;
